import { UseFormReturn } from "react-hook-form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import { ProfileFormData } from "@core/features/profile";

export type { ProfileFormData };

export type Props = {
  /** Form instance from useForm */
  form: UseFormReturn<ProfileFormData>;
  /** Whether the form has been modified */
  isDirty: boolean;
  /** Whether the form is valid */
  isValid: boolean;
  /** Whether the form is submitting */
  isSubmitting: boolean;
  /** Handler for form submission */
  onSubmit: () => void;
  /** Handler for cancel/reset */
  onCancel: () => void;
  /** Whether to show the cancel button */
  showCancelButton?: boolean;
  /** Custom primary button text */
  primaryButtonText?: string;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
};

/**
 * Profile form component that renders profile fields.
 * This is a presentational component - business logic should be in the container.
 *
 * Note: Timezone, CV, and notification settings have been moved to the
 * Settings page (/account) for better organization.
 */
export function ProfileForm({
  form,
  isDirty,
  isValid,
  isSubmitting,
  onSubmit,
  onCancel,
  showCancelButton,
  primaryButtonText = "Save Profile",
  showActionButtons = true,
}: Props) {
  // Determine whether to show cancel button
  const shouldShowCancel = showCancelButton ?? isDirty;

  return (
    <div className="flex flex-col gap-4" data-testid="profile-form">
      <Form form={form} errorContainerClassName="p-0 w-full max-w-full mt-8">
        <FormField
          name="firstName"
          label="First Name"
          component="Input"
          placeholder="Enter your first name"
          layout="horizontal"
          required
        />
        <FormField
          name="lastName"
          label="Last Name"
          component="Input"
          placeholder="Enter your last name"
          layout="horizontal"
          required
        />
        <FormField
          name="photo"
          label="Photo"
          component="PhotoUploadInput"
          placeholder="Upload your photo"
          layout="horizontal"
          requireAltText
        />
        <FormField
          name="pronouns"
          label="Pronouns"
          component="Input"
          placeholder="Your pronouns"
          layout="horizontal"
        />
      </Form>
      {showActionButtons && (
        <ActionButtonsGroup
          primaryButtonText={primaryButtonText}
          onPrimaryClick={onSubmit}
          secondaryButtonText={shouldShowCancel ? "Cancel" : undefined}
          onSecondaryClick={shouldShowCancel ? onCancel : undefined}
          className="size-max w-fit self-end"
          primaryButtonProps={{
            disabled: !isDirty || isSubmitting,
            visuallyDisabled: isDirty && !isSubmitting && !isValid,
          }}
        />
      )}
    </div>
  );
}

export default ProfileForm;
